import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { StateChart, type ChartDay } from '../../components/state/StateChart'
import { StateMap, type MapDay } from '../../components/state/StateMap'
import { HelpButton, StateTip } from '../../components/state/StateTip'
import { COPY, SCORE_KEYS } from '../../components/state/stateCopy'
import { ProblemNotice, ResearchMarker, ResearchNotes, Tag } from '../../components/ui'
import { useHomeSummary } from '../../contexts/HomeSummaryContext'
import { useTheme } from '../../contexts/ThemeContext'
import { getBaselineStatus, getDailyStatus, getRecording, listRecordings, type BaselineStatus, type RecordingRecord } from '../../lib/api'
import { readDailyStatus, type DailyStatusResult } from '../../lib/dailyStatus'
import { DEVICE_LABELS } from '../../lib/devices'
import { formatAxisValue2, formatScore, stateDateRange, stateDateWords } from '../../lib/format'
import { cornerWords, hasStateMap, mapLocked, stateViewOf, type HomeReadingView, type HomeSummaryView, type StateView } from '../../lib/home'
import { CONDITION_WORDS } from '../../lib/recordings'
import { stateJournal } from '../../lib/stateJournal'
import { STATE_HISTORY_PAGE_SIZE, stateRecentRows } from '../../lib/stateRecent'
import { stepTo } from '../../lib/stateGeometry'
import { useFanOut } from '../../lib/useFanOut'
import { usePagedList, type Page } from '../../lib/usePagedList'
import { useResource } from '../../lib/useResource'
import { NotFound } from '../NotFound'

/**
 * `/state`: the owner's approved prototype, exactly (R72, 2026-10-01;
 * `_design/state-prototype/brainspot-patterns-styled.html`). Its words are
 * `components/state/stateCopy.ts`; everything else on the page is a stored
 * reading — the payload's word, line, axis names, corner words and values,
 * and the person's own journal answers.
 *
 * Truth rules that outrank the look: a missing value is never drawn as a
 * value (no dot, no point, nothing at zero), the values shown are the stored
 * ones, and the learning lock still hides the map until the eyes-closed
 * baseline is confirmed.
 */
const SAMPLE = import.meta.env.VITE_API_MOCKS === 'true'

export function State() {
  const { state, reload } = useHomeSummary()
  if (state.kind === 'loading') return <div className="stack stack--wide bp-state"><Heading condition={null} /><p role="status">Loading your state map…</p></div>
  if (state.kind === 'failed') return <div className="stack stack--wide bp-state"><Heading condition={null} />
    {state.error != null ? <ProblemNotice error={state.error} onRetry={reload}>Your state map could not be loaded.</ProblemNotice>
      : <div className="bp-warning">{state.detail}</div>}</div>
  if (state.kind !== 'ready' || !hasStateMap(state) || state.summary.reading === null) return <NotFound />
  return <StatePage key={`${state.summary.userId}/${state.summary.reading.recordingId}`}
    summary={state.summary} reading={state.summary.reading} reloadError={state.reloadError} onRetry={reload} />
}

function Heading({ condition, range, device }: { condition: string | null; range?: string | null; device?: string | null }) {
  return <div className="bp-heading">
    <div>
      <div className="bp-pagebrow">{condition === null ? 'STATE' : `STATE · ${condition.toUpperCase()}`}</div>
      <h1>State</h1>
      <p className="bp-subtitle">{COPY.subtitle}</p>
    </div>
    {(range != null || device != null) && <div className="bp-meta">
      {SAMPLE && <span className="bp-sample">{COPY.sample}</span>}
      {range != null && <strong>{range}</strong>}{device}
    </div>}
  </div>
}

const lowerFirst = (text: string) => /^[A-Z][a-z]/.test(text) ? `${text.charAt(0).toLowerCase()}${text.slice(1)}` : text

/** The stored scores the detail panel shows, in the prototype's order, by `ui_scores`' own titles. */
function scoresOf(result: DailyStatusResult | null): Array<{ key: string; title: string; value: number | null }> {
  if (result === null || !result.readable) return []
  const ui = result.research.uiScores as unknown as {
    display?: Record<string, { title?: unknown }>; scores?: Record<string, unknown>; validity?: Record<string, { suppressed?: unknown; valid?: unknown }>
  } | null
  if (ui == null || typeof ui !== 'object' || ui.display == null) return []
  return SCORE_KEYS.flatMap((key) => {
    const title = ui.display?.[key]?.title
    if (typeof title !== 'string') return []
    const raw = ui.scores?.[key]
    const v = ui.validity?.[key]
    const withheld = v?.suppressed === true || v?.valid === false
    return [{ key, title, value: !withheld && typeof raw === 'number' && Number.isFinite(raw) ? raw : null }]
  })
}

function StatePage({ summary, reading, reloadError, onRetry }: {
  summary: HomeSummaryView
  reading: HomeReadingView
  reloadError: unknown
  onRetry: () => void
}) {
  const { resolved } = useTheme()
  const [selectedId, setSelectedId] = useState(reading.recordingId)
  const [report, setReport] = useState(false)
  // The prototype's two report-panel moves: opening scrolls the panel into view
  // (`scrollIntoView({block:'nearest',behavior:'smooth'})`), Close hands focus
  // back to "Explore this reading". Only on those clicks, never on a re-render.
  const exploreRef = useRef<HTMLButtonElement>(null)
  const reportRef = useRef<HTMLElement>(null)
  const revealReport = useRef(false)
  useEffect(() => {
    if (!report || !revealReport.current) return
    revealReport.current = false
    reportRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [report])
  const latestView = stateViewOf(reading.result, summary.display)
  const anchorLoad = useCallback((signal: AbortSignal) => getRecording(reading.recordingId, { signal }), [reading.recordingId])
  const anchor = useResource(anchorLoad)
  const pageLoad = useCallback((cursor: string | null, signal: AbortSignal): Promise<Page<RecordingRecord>> =>
    listRecordings(summary.userId, { cursor, limit: STATE_HISTORY_PAGE_SIZE }, { signal }), [summary.userId])
  const list = usePagedList(pageLoad)
  const baselineLoad = useCallback((signal: AbortSignal): Promise<BaselineStatus | null> => {
    if (anchor.data === null) return Promise.resolve(null)
    return summary.baseline.deviceId === anchor.data.device_id
      ? Promise.resolve(summary.baseline.EC)
      : getBaselineStatus(summary.userId, 'EC', { deviceId: anchor.data.device_id, signal })
  }, [anchor.data, summary.baseline, summary.userId])
  const baseline = useResource(baselineLoad)
  const rows = useMemo(() => anchor.data === null ? [] : stateRecentRows(anchor.data, list.items, selectedId),
    [anchor.data, list.items, selectedId])
  const keys = useMemo(() => rows.filter((row) => row.recording_id !== reading.recordingId).map((row) => row.recording_id),
    [rows, reading.recordingId])
  const fetchDay = useCallback((id: string, signal: AbortSignal) => getDailyStatus(id, { signal }).then(readDailyStatus), [])
  const eligible = useCallback(() => true, [])
  const fan = useFanOut<string, DailyStatusResult>(keys, fetchDay, { eligible })
  const condition = CONDITION_WORDS[summary.condition]

  if (anchor.isLoading || anchor.data === null) return <div className="stack stack--wide bp-state" data-testid="state-view">
    <Heading condition={condition} />
    {anchor.error !== null ? <ProblemNotice error={anchor.error} onRetry={anchor.reload}>This reading's recording details could not be loaded.</ProblemNotice>
      : <p role="status">Loading recording details…</p>}
  </div>

  const locked = baseline.isLoading || baseline.error !== null || baseline.data === null || mapLocked(baseline.data)
  const found = rows.findIndex((row) => row.recording_id === selectedId)
  const selectedIndex = found === -1 ? rows.length - 1 : found
  const selected = rows[selectedIndex]
  const resultOf = (id: string): DailyStatusResult | null => id === reading.recordingId ? reading.result
    : fan.entries[id]?.status === 'ready' ? fan.entries[id].value ?? null : null
  const viewOf = (id: string): StateView | null => stateViewOf(resultOf(id), summary.display)
  const selectedResult = resultOf(selected.recording_id)
  const selectedView = viewOf(selected.recording_id)
  const frameView = selectedView ?? latestView
  const clip = latestView?.map.clip ?? null
  const axisOf = (view: StateView | null, key: 'activation' | 'balance') => view?.axes.find((a) => a.key === key) ?? null
  const names = {
    activation: axisOf(frameView, 'activation')?.name ?? 'activation',
    balance: axisOf(frameView, 'balance')?.name ?? 'balance',
  }
  const ends = { activation: axisOf(frameView, 'activation')?.ends ?? null, balance: axisOf(frameView, 'balance')?.ends ?? null }

  const days = rows.map((row) => {
    const view = viewOf(row.recording_id)
    const words = stateDateWords(row.session_date)
    return { row, view, words, act: view?.map.act ?? null, bal: view?.map.bal ?? null }
  })
  const mapDays: MapDay[] = days.map((d) => ({ id: d.row.recording_id, short: d.words.short, long: d.words.long,
    act: locked ? null : d.act, bal: locked ? null : d.bal, word: d.view?.map.word ?? null }))
  const chartDays = (key: 'act' | 'bal'): ChartDay[] => days.map((d) => ({ id: d.row.recording_id, date: d.row.session_date,
    dayNum: d.words.day, short: d.words.short, long: d.words.long, value: d[key] }))
  const stored = (key: 'act' | 'bal') => days.filter((d) => d[key] !== null).length
  const selectedWords = stateDateWords(selected.session_date)
  const badge = frameView?.map.badge ?? null
  // R73 (owner instruction 2026-10-01, overriding the prototype's pill on this one point): the map
  // carries the small research marker the other pages use, and the words sit in the notes at the
  // bottom of the page. `null`, the stored token, or a token this client does not know.
  const research = badge === null || frameView?.map.validated === true ? null : badge
  const journal = stateJournal(selected.self_report, COPY.notReported)
  const scores = scoresOf(selectedResult)
  const device = DEVICE_LABELS[anchor.data.device_id] ?? anchor.data.device_id
  const range = stateDateRange(rows[0].session_date, rows[rows.length - 1].session_date)
  const status = fan.entries[selected.recording_id]?.status
  const select = (id: string) => setSelectedId(id)
  const step = (delta: -1 | 1) => {
    const to = stepTo(selectedIndex, delta, rows.length)
    if (to !== null) select(rows[to].recording_id)
  }

  // The stored reasons an axis is missing, in the payload's sentences: "Balance unavailable: …".
  const warnings = selectedView === null || locked ? [] : selectedView.axes.flatMap((axis) =>
    axis.value === null && axis.reasonLine !== null && axis.reasonLine !== selectedView.map.line
      ? [`${axis.name ?? axis.key} unavailable: ${lowerFirst(axis.reasonLine)}`] : [])

  /** Why an axis has no series at all: each distinct stored reason line across the loaded days. */
  const missingReasons = (key: 'activation' | 'balance') => [...new Set(days.flatMap((d) => {
    const line = d.view?.axes.find((a) => a.key === key)?.reasonLine ?? null
    return line === null ? [] : [line]
  }))]

  const chartBlock = (key: 'activation' | 'balance') => {
    const k = key === 'activation' ? 'act' : 'bal'
    const name = names[key]
    const count = stored(k)
    const value = selectedView === null ? null : axisOf(selectedView, key)?.value ?? null
    const end = ends[key]
    return <div data-testid={`state-trend-${key}`}>
      <div className="bp-charthead">{name} <strong>{value === null ? COPY.unavailable : `${formatAxisValue2(value)} z`}</strong></div>
      {count === 0
        ? <div className="bp-missing" data-testid={`state-missing-${key}`}>
          <p>{`No ${name.toLowerCase()} value is stored for ${rows.length === 1 ? 'this reading' : 'these readings'} yet, so there is no timeline to draw.`}</p>
          {missingReasons(key).map((line) => <p key={line} className="bp-missing__why">{line}</p>)}
        </div>
        : <>
          {clip !== null && <StateChart axis={key} name={name} days={chartDays(k)} selectedIndex={selectedIndex}
            clip={clip} single={count === 1} onSelect={select} />}
          {count === 1 && <p className="bp-chartnote" data-testid={`state-single-${key}`}>
            {`One stored ${name.toLowerCase()} value so far: not enough values for a trend.`}
          </p>}
        </>}
      {end !== null && count > 0 && <div className="bp-chartfoot">{`Higher: ${end.pos.toLowerCase()} · lower: ${end.neg.toLowerCase()}`}</div>}
    </div>
  }

  return <div className="stack stack--wide bp-state" data-testid="state-view" data-ready="true" data-painted={resolved}>
    <Heading condition={condition} range={range} device={device} />
    {reloadError != null && <ProblemNotice error={reloadError} onRetry={onRetry}>Your summary could not be refreshed.</ProblemNotice>}
    {baseline.error !== null && <ProblemNotice error={baseline.error} onRetry={baseline.reload}>The baseline for this headband could not be loaded.</ProblemNotice>}
    {list.error !== null && <ProblemNotice error={list.error} onRetry={list.items.length === 0 ? list.reload : list.loadMore}>More recordings could not be loaded.</ProblemNotice>}
    <div className="bp-grid grid-row">
      <section className="bp-panel" aria-label="Recent readings on the state map" data-testid="state-reading">
        <div className="bp-panelhead">
          <div className="bp-headgroup"><h2>{COPY.mapHeading}</h2><HelpButton label={COPY.mapHelpLabel} text={COPY.mapHelp} placement="bottom" /></div>
          {research !== null && <ResearchMarker />}
        </div>
        <p className="bp-mapnote">{locked ? COPY.locked : COPY.mapNote}</p>
        <StateMap days={mapDays} selectedId={selected.recording_id} clip={clip} dz={latestView?.map.dz ?? null}
          corners={cornerWords(frameView?.map.corners ?? null)} ends={ends.activation} names={names}
          locked={locked} noPosition={COPY.noPosition} onSelect={select} />
        <div className="bp-dates" role="group" aria-label={COPY.datesLabel}>
          {days.map((d) => {
            const empty = d.act === null || d.bal === null
            return <button type="button" className="bp-day" key={d.row.recording_id} data-empty={empty ? 'true' : 'false'}
              aria-pressed={d.row.recording_id === selected.recording_id} onClick={() => select(d.row.recording_id)}
              aria-label={`${d.words.full}${empty && !locked ? ', map position unavailable' : ''}`}>
              <span>{d.words.weekdayShort}</span><strong>{d.words.day}</strong>
            </button>
          })}
        </div>
        {!list.isComplete && <button type="button" className="bp-more" onClick={list.loadMore}
          disabled={list.isPaging || list.isLoading}>{COPY.loadMore}</button>}
      </section>

      <section className="bp-panel bp-detail" aria-label="Selected reading" aria-live="polite" data-testid="state-selected">
        <div className="bp-detailtop">
          <div><div className="bp-brow">{COPY.selectedBrow}</div><h2>{selectedWords.full}</h2></div>
          <div className="bp-step">
            <button type="button" aria-label={COPY.previous} disabled={stepTo(selectedIndex, -1, rows.length) === null} onClick={() => step(-1)}>‹</button>
            <button type="button" aria-label={COPY.next} disabled={stepTo(selectedIndex, 1, rows.length) === null} onClick={() => step(1)}>›</button>
          </div>
        </div>
        {locked ? <p className="bp-explain">{COPY.locked}</p>
          : selectedView === null ? <p className="bp-explain" role="status">{status === 'error'
            ? 'This result could not be loaded.' : selectedResult?.readable === false ? selectedResult.detail
              : status === 'ready' ? 'No state map is stored for this reading.' : 'Loading this reading…'}
            {status === 'error' && <> <button type="button" className="bp-more" onClick={() => fan.retry(selected.recording_id)}>Try again</button></>}</p>
            : <>
              <div className="bp-stateword" data-testid="state-word"><span />{selectedView.map.word ?? ''}</div>
              <p className="bp-explain" data-testid="state-line">{selectedView.map.line ?? ''}</p>
              <div className="bp-axisrows" data-testid="state-axes">
                {(['activation', 'balance'] as const).map((key) => {
                  const value = axisOf(selectedView, key)?.value ?? null
                  return <div className="bp-axisrow" key={key} data-axis={key}>{names[key]}{' '}
                    <HelpButton label={key === 'activation' ? COPY.activationHelpLabel : COPY.balanceHelpLabel}
                      text={key === 'activation' ? COPY.activationHelp : COPY.balanceHelp} />
                    <span className="bp-axisval">{value === null ? COPY.unavailable : <>{formatAxisValue2(value)} <span className="bp-unit">z</span></>}</span>
                  </div>
                })}
              </div>
              {warnings.map((text) => <div className="bp-warning" key={text} data-testid="state-warnings">{text}</div>)}
            </>}
        <div className="bp-context" data-testid="state-journal">
          <div className="bp-headgroup"><h3>{COPY.reported}</h3><HelpButton label={COPY.reportedHelpLabel} text={COPY.reportedHelp} /></div>
          {journal === null ? <p className="bp-note">{COPY.noCheckIn}</p> : <>
            <dl className="bp-contextgrid">
              <div><dt>{COPY.sleep}</dt><dd>{journal.sleep}</dd></div>
              <div><dt>{COPY.level}</dt><dd>{journal.level}</dd></div>
              <div><dt>{COPY.caffeine}</dt><dd>{journal.caffeine}</dd></div>
            </dl>
            {journal.note !== null && <p className="bp-note">{`“${journal.note}”`}</p>}
          </>}
        </div>
        <button ref={exploreRef} type="button" className="bp-link" aria-expanded={report} aria-controls="bp-report"
          onClick={() => { revealReport.current = !report; setReport(!report) }}>{COPY.explore} <span aria-hidden="true">↗</span></button>
      </section>
    </div>

    {!locked && <section className="bp-panel bp-trends" aria-label="Activation and balance over time" data-testid="state-trends">
      <div className="bp-trendhead">
        <div className="bp-headgroup"><h2>{COPY.trendsHeading}</h2><HelpButton label={COPY.trendsHelpLabel} text={COPY.trendsHelp} placement="bottom" /></div>
        <span className="bp-small">{`${rows.length} recorded ${rows.length === 1 ? 'day' : 'days'}${list.isComplete ? '' : ' loaded so far'}`}</span>
      </div>
      <div className="bp-trendgrid">{chartBlock('activation')}{chartBlock('balance')}</div>
    </section>}

    <section ref={reportRef} id="bp-report" className="bp-panel bp-report" hidden={!report} aria-label="Selected recording detail preview" data-testid="state-report">
      <div className="bp-reporthead">
        <div><div className="bp-brow">{SAMPLE ? COPY.reportBrowSample : COPY.reportBrowStored}</div>
          <h2>{`${selectedWords.long}${selectedView?.map.word == null || locked ? '' : ` · ${selectedView.map.word}`}`}</h2></div>
        <button type="button" className="bp-close" onClick={() => { setReport(false); exploreRef.current?.focus() }}>{COPY.close}</button>
      </div>
      <div className="bp-scoregrid">
        {scores.map((s) => <div className="bp-score" key={s.key}><h3>{s.title}</h3>
          <strong>{s.value === null ? '—' : formatScore(s.value)}</strong>
          <span>{s.value === null ? COPY.scoreMissing : SAMPLE ? COPY.scoreSample : COPY.scoreStored}</span></div>)}
      </div>
    </section>

    {research !== null && <ResearchNotes testid="state-research-notes" label={COPY.research}>
      <p className="research-notes__item">{research === 'research_only' ? COPY.researchHelp
        : <><Tag>{research}</Tag> {COPY.researchHelp}</>}</p>
    </ResearchNotes>}

    <footer className="bp-footer">
      <span>{SAMPLE ? COPY.footerSample : COPY.footerStored}</span>
      {SAMPLE && <StateTip text={COPY.aboutSampleHelp} placement="top">
        {(tip) => <button type="button" {...tip}>{COPY.aboutSample}</button>}
      </StateTip>}
    </footer>
  </div>
}
